import {
  Button,
  Icon,
  InfoCircleHollowSVG,
  TooltipDialog,
  Collapse,
  RotatingChevron,
} from "@newsela/angelou";

import useGetUserExperimentSegment from "@/features/useGetUserExperimentSegment";
import FormativeLogoIcon from "@/icons/FormativeLogoIcon";
import { AttachmentWrapper } from "@/types/models/ContentAttachment.interface";
import { UserProperties } from "@/types/models/UserProperties.interface";
import CuratedFormativeOption from "@/view/ArticleActivityPicker/CuratedFormativeOption";
import FormativeOption from "@/view/ArticleActivityPicker/FormativeOption";
import GraphicOrganizerContainer from "@/view/TeacherActivityContainer/GraphicOrganizerContainer";
import useFormativeAttachment from "@/view/utils/useFormativeAttachment";
import {
  collapseBtnCss,
  formativeHeaderCss,
  formativeIconCss,
  headingCss,
  tooltipIconCss,
} from "./FormtativeItemsContainer.style";

interface FormativeItemsContainerProps {
  attachments: AttachmentWrapper[];
  contentId: string;
  formativeJwtResponse: {
    url: string;
    payload: string;
  };
  modalTriggerRef?: { current: null };
  onModalOpen?: () => void;
  shouldDisplayFormativeTOS: boolean;
  userProperties: Partial<UserProperties>;
}

const FORMATIVE_CTA = "Customize this article's activities";

const FormativeItemsContainer = ({
  attachments,
  contentId,
  formativeJwtResponse,
  modalTriggerRef,
  onModalOpen,
  shouldDisplayFormativeTOS,
  userProperties,
}: FormativeItemsContainerProps) => {
  const { activeELALicense } = userProperties;

  const [isFormativeCuratedConnection] = useGetUserExperimentSegment(
    "formative_curated_connection",
  );

  const [isFormativeSectionExpandedSegment] = useGetUserExperimentSegment(
    "formative_section_expanded",
  );

  const isFormativeSectionExpanded = isFormativeSectionExpandedSegment === "on";

  const { curatedFormativeCta, formativeAttachmentStream } =
    useFormativeAttachment({ isFormativeCuratedConnection, attachments });

  const initialIsHidden = !isFormativeSectionExpanded;
  return (
    <>
      <Collapse
        __cssFor={collapseBtnCss}
        initialIsHidden={initialIsHidden}
        renderButtonContents={(isHidden) => (
          <>
            <div css={formativeHeaderCss}>
              <FormativeLogoIcon
                __cssFor={{ root: formativeIconCss }}
                size="MD"
              />
              <h3 css={headingCss}>Formative</h3>
              {/* TODO: Workaround to fix clicks on TooltipDialog being
              propagated, remove after its fixed in Angelou */}
              <div onClick={(e) => e.stopPropagation()}>
                <TooltipDialog
                  buttonContents={
                    <Icon
                      SvgComponent={InfoCircleHollowSVG}
                      __cssFor={tooltipIconCss}
                    />
                  }
                  buttonProps={{ flavor: Button.flavors.incognito }}
                  __cssFor={{
                    dialog: {
                      root: { maxWidth: "300px" },
                    },
                  }}
                  body={
                    <>
                      Use Formative to bring this article to life with
                      interactive questions and dynamic content.
                    </>
                  }
                  placement="top"
                  ariaProps={{
                    "aria-label": "About Formative",
                  }}
                />
              </div>
            </div>{" "}
            <RotatingChevron isDown={isHidden} />
          </>
        )}
      >
        {curatedFormativeCta ? (
          <CuratedFormativeOption
            contentId={contentId}
            cta={curatedFormativeCta}
            data={formativeJwtResponse}
            streamName={formativeAttachmentStream}
          />
        ) : (
          <FormativeOption
            contentId={contentId}
            ctaText={FORMATIVE_CTA}
            data={formativeJwtResponse}
            {...(shouldDisplayFormativeTOS && {
              onModalOpen,
              modalTriggerRef,
            })}
          />
        )}
        {activeELALicense && (
          <GraphicOrganizerContainer
            contentId={contentId}
            userProperties={userProperties}
          />
        )}
      </Collapse>
    </>
  );
};

export default FormativeItemsContainer;
